/**
 * @file PlanFilesManager.js
 * @description Shared component for managing files attached to a plan entity
 * (plan year or benefit plan). Encapsulates the file list, add popover, upload
 * form, and delete flow. Consumers customize the row rendering and entity
 * label through props.
 */

import { useState, useEffect, useContext } from 'react';
import { Popover, Box } from '@mui/material';
import AssetForm, { ASSET_FORM_MODE } from './AssetForm';
import NotFoundBox from '../../ui/no_data_indicators/NotFoundBox';
import Spinner from '../../ui/spinners/Spinner';
import Toast from '../../ui/toasts/Toast';
import { ModalContext, ModalTypes } from '../../../store/ModalContext';
import { fetchAssetByID, updateAsset } from '../../../services/clientService';
import { logEvent } from '../../../services/loggingService';
import { LogEventType } from '../../../utils/data/enums';
import {
    canRenderAssetPreviewInBrowser,
    getFileCategory,
    processFileName,
} from '../../helpers/FileUtils';
import styles from '../PlanYear.module.scss';
import modalStyles from '../../ui/modals/modals.module.scss';
import FullscreenDisplay from '../../assets/shared/FullscreenDisplay';
import { getText } from '../../../i18n';

const PlanFilesManager = ({
    data,
    onChange,
    storageKey,
    entityLabel,
    entityLabelCapitalized,
    headerTitle,
    headerDescription,
    logContext,
    renderFileRow,
    showPopulationGroups = false,
    persistPopulationGroupsOnAsset = true,
    showNameAndTagsFields = false,
}) => {
    const { showModal } = useContext(ModalContext);
    const [files, setFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formMode, setFormMode] = useState(null);
    const [editingAsset, setEditingAsset] = useState(null);
    const [editingAssetId, setEditingAssetId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [addPopoverOpen, setAddPopoverOpen] = useState(false);
    const addPopoverId = addPopoverOpen ? 'add-files-popover' : undefined;
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fullscreenImageUrl, setFullscreenImageUrl] = useState('');
    const [fullscreenMediaType, setFullscreenMediaType] = useState('');

    const mapAttachmentPopulationGroups = attachment => {
        if (!attachment) return [];

        if (Array.isArray(attachment.populationGroupIds)) {
            return attachment.populationGroupIds
                .filter(Boolean)
                .map(groupId => ({ id: +groupId || groupId }));
        }

        if (Array.isArray(attachment.assetGroups)) {
            return attachment.assetGroups
                .map(group => {
                    if (typeof group === 'object' && group !== null) {
                        return {
                            id: group.populationGroupId || group.id,
                        };
                    }
                    return { id: group };
                })
                .filter(group => group.id);
        }

        if (Array.isArray(attachment.populationGroups)) {
            return attachment.populationGroups
                .map(group => ({ id: group.populationGroupId || group.id }))
                .filter(group => group.id);
        }

        return [];
    };

    useEffect(() => {
        if (data?.id) {
            loadFiles();
        }
    }, [data?.id]);

    const loadFiles = async () => {
        if (!data?.id) return;

        setIsLoading(true);
        try {
            const attachments = data[storageKey] || [];
            if (attachments.length === 0) {
                setFiles([]);
                return;
            }

            const assetPromises = attachments.map(async attachment => {
                try {
                    const fetchedAsset = await fetchAssetByID(
                        attachment.assetId,
                        null,
                    );
                    const attachmentGroups =
                        mapAttachmentPopulationGroups(attachment);

                    if (attachmentGroups.length > 0) {
                        return {
                            ...fetchedAsset,
                            assetGroups: attachmentGroups,
                        };
                    }

                    return fetchedAsset;
                } catch (error) {
                    console.error(
                        `Error fetching asset ${attachment.assetId}:`,
                        error,
                    );
                    return null;
                }
            });

            const assetList = await Promise.all(assetPromises);
            setFiles(assetList.filter(Boolean));
        } catch (error) {
            console.error(`Failed to fetch ${entityLabel} files`, error);
            Toast.error(`There was an issue loading the ${entityLabel} files.`);
        } finally {
            setIsLoading(false);
        }
    };

    const openFullscreen = (mediaUrl, mediaType = 'image') => {
        setFullscreenImageUrl(mediaUrl);
        setFullscreenMediaType(mediaType);
        setIsFullscreen(true);
    };

    const closeFullscreen = () => {
        setIsFullscreen(false);
        setFullscreenImageUrl('');
        setFullscreenMediaType('image');
    };

    const handleDoubleClick = index => {
        const selectedFile = files[index];
        if (!isPreviewableAssetFile(selectedFile)) {
            Toast.error(getText('msg_e4a8cb9c4e93'));
            return;
        }

        if (!selectedFile?.url) {
            Toast.error(getText('msg_5281be24a65c'));
            return;
        }

        const assetCategory = getFileCategory(selectedFile.fileType);
        const canRenderDocumentInBrowser =
            assetCategory === 'document' &&
            canRenderAssetPreviewInBrowser(
                selectedFile.fileType,
                process.env.REACT_APP_DEPLOYED_ENV_NAME,
            );

        const canShowViewAction =
            assetCategory === 'image' ||
            assetCategory === 'video' ||
            canRenderDocumentInBrowser;

        if (assetCategory === 'image') {
            openFullscreen(selectedFile.url, 'image');
        } else if (assetCategory === 'video') {
            openFullscreen(selectedFile.url, 'video');
        } else {
            showModal({
                type: ModalTypes.ASSET_VIEW,
                title: selectedFile.name,
                text: selectedFile.url,
                fileType: selectedFile.fileType,
            });
        }
    };

    const isPreviewableAssetFile = file => {
        return canRenderAssetPreviewInBrowser(file?.fileType);
    };

    const handleDownloadFile = file => {
        if (!file?.url) {
            Toast.error(getText('msg_3a39ab06f745'));
            return;
        }

        const processedName = processFileName(file.name, file.fileType);

        fetch(file.url)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response failed');
                }
                return response.blob();
            })
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = processedName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
            })
            .catch(error => {
                console.error(
                    'There was a problem downloading the file:',
                    error,
                );
                Toast.error(getText('msg_e354a74956ef'));
            });
    };

    const handleClickOnDelete = fileToDelete => {
        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_b957efeb6067'),
            text: `This action will remove the file "${fileToDelete.name}" from this ${entityLabel} and cannot be undone.`,
            onYes: () => handleDeleteFile(fileToDelete.id),
            onNo: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_e963907dac5c'),
        });
        if (logContext) {
            logEvent(LogEventType.DeleteClicked, logContext);
        }
    };

    const handleDeleteFile = async assetId => {
        setIsLoading(true);
        try {
            const updatedAttachments = (data[storageKey] || []).filter(
                attachment => attachment.assetId !== assetId,
            );

            onChange({
                ...data,
                [storageKey]: updatedAttachments,
            });

            setFiles(prevFiles =>
                prevFiles.filter(file => file.id !== assetId),
            );
        } catch (error) {
            console.error(`Failed to remove file from ${entityLabel}`, error);
            Toast.error(
                `There was an issue removing the file from the ${entityLabel}.`,
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddButtonClick = event => {
        setAnchorEl(event.currentTarget);
        setAddPopoverOpen(prev => !prev);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setAddPopoverOpen(false);
    };

    const handleAddFromAssets = () => {
        handlePopoverClose();
        showModal({
            type: ModalTypes.ADD_FROM_ASSETS,
            title: getText('msg_0bc609667aa7'),
            subtitle: showPopulationGroups
                ? 'Only the population group selected will see the Asset when viewing the Benefits module.'
                : null,
            onYes: async selectedAsset => {
                try {
                    const isAlreadyAttached = (data[storageKey] || []).some(
                        attachment => attachment.assetId === selectedAsset.id,
                    );

                    if (isAlreadyAttached) {
                        Toast.warning(
                            `This asset is already attached to the ${entityLabel}`,
                        );
                        return;
                    }

                    const currentAttachments = data[storageKey] || [];
                    const selectedPopulationGroupIds = Array.isArray(
                        selectedAsset.selectedPopulationGroupIds,
                    )
                        ? selectedAsset.selectedPopulationGroupIds
                        : [];

                    const selectedGroups = Array.isArray(
                        selectedAsset.selectedPopulationGroups,
                    )
                        ? selectedAsset.selectedPopulationGroups
                        : [];

                    if (
                        showPopulationGroups &&
                        persistPopulationGroupsOnAsset &&
                        selectedPopulationGroupIds.length > 0
                    ) {
                        const updateResponse = await updateAsset(
                            {
                                ...selectedAsset,
                                assetGroups: selectedPopulationGroupIds,
                            },
                            null,
                        );

                        if (!updateResponse || updateResponse.error) {
                            throw new Error(
                                updateResponse?.message ||
                                    'Failed to update asset population groups',
                            );
                        }
                    }

                    onChange({
                        ...data,
                        [storageKey]: [
                            ...currentAttachments,
                            {
                                assetId: selectedAsset.id,
                                ...(showPopulationGroups
                                    ? {
                                          populationGroupIds:
                                              selectedPopulationGroupIds,
                                      }
                                    : {}),
                            },
                        ],
                    });

                    setFiles(prevFiles => [
                        ...prevFiles,
                        {
                            ...selectedAsset,
                            ...(selectedGroups.length > 0
                                ? { assetGroups: selectedGroups }
                                : {}),
                        },
                    ]);
                } catch (error) {
                    console.error('Failed to add asset from library', error);
                    Toast.error(getText('msg_2a03d53f2b4b'));
                }
            },
            onNo: () => {},
            onHide: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_61cc55aa0453'),
            acceptedFileTypes: [],
            assetTypes: [],
            wasCreatedByMe: false,
            requirePopulationGroupSelectionForImages: showPopulationGroups,
        });
    };

    const handleUploadFromDevice = () => {
        setFormMode(ASSET_FORM_MODE.ADD);
        setEditingAsset(null);
        setEditingAssetId(null);
    };

    const handleEditFile = fileToEdit => {
        const normalizedAssetId = fileToEdit?.id || fileToEdit?.assetId || null;

        if (!normalizedAssetId) {
            Toast.error(getText('msg_56307f143715'));
            return;
        }

        const attachmentForAsset = (data[storageKey] || []).find(
            attachment => attachment.assetId === normalizedAssetId,
        );
        const attachmentGroups =
            mapAttachmentPopulationGroups(attachmentForAsset);

        setFormMode(ASSET_FORM_MODE.EDIT);
        setEditingAsset({
            ...fileToEdit,
            id: normalizedAssetId,
            assetId: normalizedAssetId,
            ...(showPopulationGroups && !persistPopulationGroupsOnAsset
                ? {
                      assetGroups: attachmentGroups,
                  }
                : {}),
        });
        setEditingAssetId(normalizedAssetId);
    };

    const handleFormSaved = (savedAsset, mode) => {
        const selectedPopulationGroups = Array.isArray(savedAsset?.assetGroups)
            ? savedAsset.assetGroups
                  .map(group => {
                      if (typeof group === 'object' && group !== null) {
                          const groupId = group.populationGroupId || group.id;
                          if (!groupId) {
                              return null;
                          }

                          return {
                              id: groupId,
                              name: group.name,
                          };
                      }

                      return {
                          id: group.toString(),
                          name: undefined,
                      };
                  })
                  .filter(Boolean)
            : [];

        const selectedPopulationGroupIds = Array.isArray(
            savedAsset?.assetGroups,
        )
            ? savedAsset.assetGroups
                  .map(group => {
                      if (typeof group === 'object' && group !== null) {
                          return group.populationGroupId || group.id;
                      }

                      return group;
                  })
                  .filter(Boolean)
                  .map(groupId => groupId.toString())
            : [];

        if (mode === ASSET_FORM_MODE.ADD) {
            const newAssetId = savedAsset.id;
            const currentAttachments = data[storageKey] || [];

            onChange({
                ...data,
                [storageKey]: [
                    ...currentAttachments,
                    {
                        assetId: newAssetId,
                        ...(showPopulationGroups
                            ? {
                                  populationGroupIds:
                                      selectedPopulationGroupIds,
                                  populationGroups: selectedPopulationGroups,
                              }
                            : {}),
                    },
                ],
            });

            setFiles(prevFiles => [...prevFiles, savedAsset]);
        } else {
            if (showPopulationGroups) {
                const currentAttachments = data[storageKey] || [];
                const updatedAttachments = currentAttachments.map(attachment =>
                    attachment.assetId === savedAsset.id
                        ? {
                              ...attachment,
                              populationGroupIds: selectedPopulationGroupIds,
                              populationGroups: selectedPopulationGroups,
                          }
                        : attachment,
                );

                onChange({
                    ...data,
                    [storageKey]: updatedAttachments,
                });
            }

            setFiles(prevFiles =>
                prevFiles.map(file =>
                    file.id === savedAsset.id
                        ? { ...file, ...savedAsset }
                        : file,
                ),
            );
        }

        setFormMode(null);
        setEditingAsset(null);
        setEditingAssetId(null);
    };

    const handleCancelForm = () => {
        setFormMode(null);
        setEditingAsset(null);
        setEditingAssetId(null);
    };

    const renderDefaultFileRow = (
        file,
        index,
        { onDelete, onPreview, onEdit, onDownload },
    ) => (
        <div key={file.id} className={styles.fileBox}>
            <div className={styles.fileName}>
                {file.name}
                {file.fileType && `.${file.fileType}`}
            </div>
            <div className={styles.fileActions}>
                <button
                    type="button"
                    className={styles.actionIcon}
                    onClick={() => onDelete(file)}
                    title={getText('msg_b9ea4b3f01ac')}
                >
                    <i className="bi bi-trash3"></i>
                </button>

                <button
                    type="button"
                    className={styles.actionIcon}
                    onClick={() => onDownload(file)}
                    title={getText('msg_774025da27ed')}
                >
                    <i className="bi bi-cloud-download"></i>
                </button>

                {onEdit && (
                    <button
                        type="button"
                        className={styles.actionIcon}
                        onClick={() => onEdit(file)}
                        title={getText('msg_43b9013be021')}
                    >
                        <i className="bi bi-pencil"></i>
                    </button>
                )}
            </div>
        </div>
    );

    const renderRow = renderFileRow || renderDefaultFileRow;

    return (
        <div className="col-12 col-xs-12 col-md-12 col-lg-8 m-auto pb-5">
            {(isLoading || isSaving) && (
                <div className="text-center my-4">
                    <Spinner width={50} className={styles.loadingSpinner} />
                </div>
            )}

            {!isLoading && (
                <>
                    <div className="mb-5 pt-4">
                        <FullscreenDisplay
                            isFullscreen={isFullscreen}
                            fullscreenImageUrl={fullscreenImageUrl}
                            mediaType={fullscreenMediaType}
                            closeFullscreen={closeFullscreen}
                        />
                        <h5>{headerTitle}</h5>
                        <p className="text-muted">{headerDescription}</p>
                        <div className="d-flex justify-content-end">
                            <span
                                onClick={handleAddButtonClick}
                                role="button"
                                tabIndex={0}
                                className="button-base ing-button-primary-left"
                            >
                                <i className="bi bi-plus-lg me-1" />
                                {getText('msg_61cc55aa0453')}
                            </span>
                            <Popover
                                id={addPopoverId}
                                open={addPopoverOpen}
                                anchorEl={anchorEl}
                                onClose={handlePopoverClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: 0,
                                        paddingBottom: 0,
                                        paddingTop: 0,
                                        lineHeight: '40px',
                                    }}
                                >
                                    <div
                                        onClick={handleAddFromAssets}
                                        role="button"
                                        tabIndex={0}
                                        className={`pointer-cursor profile-link ${styles.addPopoverOption}`}
                                    >
                                        {getText('msg_0bc609667aa7')}
                                    </div>
                                    <div
                                        onClick={() => {
                                            handleUploadFromDevice();
                                            handlePopoverClose();
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        className={`pointer-cursor profile-link ${styles.addPopoverOption}`}
                                    >
                                        {getText('msg_c002fe6df29c')}
                                    </div>
                                </Box>
                            </Popover>
                        </div>
                    </div>

                    {files.length === 0 && (
                        <NotFoundBox
                            backgroundColor="#f0f4ff"
                            icon="asset"
                            title={getText('msg_78a364030673')}
                            description={getText('msg_2e48ec748490')}
                        />
                    )}

                    {files.length > 0 && (
                        <div className={styles.filesContainer}>
                            <div className={styles.filesList}>
                                {files.map((file, index) =>
                                    renderRow(file, index, {
                                        onDelete: handleClickOnDelete,
                                        onPreview: handleDoubleClick,
                                        onEdit: handleEditFile,
                                        onDownload: handleDownloadFile,
                                        canPreview: isPreviewableAssetFile,
                                    }),
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}

            {formMode && (
                <div className={`${modalStyles.container} modal-backdrop`}>
                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        aria-modal="true"
                        role="dialog"
                    >
                        <div
                            className={`modal-dialog modal-lg modal-dialog-centered ${modalStyles.modalSizing}`}
                        >
                            <div
                                className={`modal-header ${modalStyles.modalHeader}`}
                            ></div>
                            <div
                                className={`modal-content ${modalStyles.modalContent}`}
                            >
                                <button
                                    type="button"
                                    className={`ing-text-buttons-save absolute-position pointer-cursor ${modalStyles.closeAction}`}
                                    onClick={handleCancelForm}
                                    aria-label={getText('msg_bbfa773e5a63')}
                                >
                                    <i
                                        className={`bi bi-x ${modalStyles.closeActionIcon}`}
                                    />
                                    {getText('msg_bbfa773e5a63')}
                                </button>
                                <div className="modal-body">
                                    <AssetForm
                                        mode={formMode}
                                        existingAsset={editingAsset}
                                        assetId={editingAssetId}
                                        entityLabel={entityLabel}
                                        showPopulationGroups={
                                            showPopulationGroups
                                        }
                                        persistPopulationGroupsOnAsset={
                                            persistPopulationGroupsOnAsset
                                        }
                                        showNameField={showNameAndTagsFields}
                                        showTagsField={showNameAndTagsFields}
                                        onSaved={handleFormSaved}
                                        onCancel={handleCancelForm}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlanFilesManager;
